import React, { useMemo, useState, useCallback } from "react";
import { useTranslate } from "@refinedev/core";
import {
  Box,
  Chip,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  Button,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PlaceIcon from "@mui/icons-material/Place";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Circle,
  Polygon,
  Popup,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useParams, useNavigate } from "react-router-dom";
import { useForbiddenZonePath } from "../../hooks/useForbiddenZonePath";
import { EVENT_COLORS } from "../../constants/eventColors";

import "leaflet/dist/leaflet.css";
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

dayjs.extend(duration);

type MarkerType = "entry" | "exit" | "current" | null;

const MARKER_COLORS: Record<Exclude<MarkerType, null>, string> = {
  entry: "#4CAF50", // green
  exit: "#F44336", // red
  current: "#2196F3", // blue
};

const markerIcon = (color: string, label: string, highlighted = false) => {
  const size = highlighted ? 44 : 32;
  const shadow = highlighted
    ? `box-shadow:0 0 12px 4px ${color}88;`
    : "box-shadow:0 2px 6px rgba(0,0,0,0.3);";
  const zIndex = highlighted ? "z-index:1000;" : "";
  return L.divIcon({
    html: `<div style="background:${color}; color:white; border-radius:50%; width:${size}px; height:${size}px; display:flex; align-items:center; justify-content:center; font-size:${highlighted ? 16 : 14}px; font-weight:bold; border:2px solid white; ${shadow} transition:all 0.2s ease; ${zIndex}">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "",
  });
};

/** Inner component that reacts to highlighted marker changes and pans the map */
const MapHighlighter: React.FC<{
  highlighted: MarkerType;
  positions: Record<Exclude<MarkerType, null>, [number, number] | null>;
}> = ({ highlighted, positions }) => {
  const map = useMap();

  React.useEffect(() => {
    if (highlighted && positions[highlighted]) {
      map.flyTo(positions[highlighted]!, map.getZoom(), { duration: 0.5 });
    }
  }, [highlighted, positions, map]);

  return null;
};

export const ForbiddenZoneShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const { entry, place, user, locations, isLoading } = useForbiddenZonePath(
    id ? parseInt(id) : undefined
  );

  const [highlightedMarker, setHighlightedMarker] = useState<MarkerType>(null);

  const orangeColor = EVENT_COLORS.forbidden_zone.main;

  const isStillInside = entry ? !entry.exitedAt : false;

  // Find closest location to enteredAt/exitedAt
  const keyLocations = useMemo(() => {
    if (!locations.length || !entry) return null;

    const enteredTime = new Date(entry.enteredAt).getTime();
    const exitedTime = entry.exitedAt
      ? new Date(entry.exitedAt).getTime()
      : null;

    let entryIdx = 0;
    let entryDiff = Infinity;
    let exitIdx = locations.length - 1;
    let exitDiff = Infinity;

    locations.forEach((loc, i) => {
      const t = new Date(loc.timestamp).getTime();
      const dEntry = Math.abs(t - enteredTime);
      if (dEntry < entryDiff) {
        entryDiff = dEntry;
        entryIdx = i;
      }
      if (exitedTime) {
        const dExit = Math.abs(t - exitedTime);
        if (dExit < exitDiff) {
          exitDiff = dExit;
          exitIdx = i;
        }
      }
    });

    return {
      entry: locations[entryIdx],
      exit: exitedTime ? locations[exitIdx] : null,
      current: !exitedTime ? locations[locations.length - 1] : null,
    };
  }, [locations, entry]);

  // Map marker positions for MapHighlighter
  const markerPositions = useMemo(() => {
    return {
      entry: keyLocations?.entry
        ? ([keyLocations.entry.latitude, keyLocations.entry.longitude] as [
            number,
            number,
          ])
        : null,
      exit: keyLocations?.exit
        ? ([keyLocations.exit.latitude, keyLocations.exit.longitude] as [
            number,
            number,
          ])
        : null,
      current: keyLocations?.current
        ? ([
            keyLocations.current.latitude,
            keyLocations.current.longitude,
          ] as [number, number])
        : null,
    };
  }, [keyLocations]);

  const handleHover = useCallback(
    (type: MarkerType) => setHighlightedMarker(type),
    []
  );
  const handleHoverEnd = useCallback(
    () => setHighlightedMarker(null),
    []
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!entry || !place) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          {translate("history.notFound", "Entry not found")}
        </Typography>
      </Box>
    );
  }

  const durationMs = entry.exitedAt
    ? new Date(entry.exitedAt).getTime() - new Date(entry.enteredAt).getTime()
    : Date.now() - new Date(entry.enteredAt).getTime();
  const dur = dayjs.duration(durationMs);
  const durationStr =
    dur.hours() > 0
      ? `${dur.hours()}h ${dur.minutes()}m`
      : `${dur.minutes()}m ${dur.seconds()}s`;

  const pathPositions: [number, number][] = locations.map((l) => [
    l.latitude,
    l.longitude,
  ]);
  const mapCenter: [number, number] = [place.latitude, place.longitude];

  const polygonPositions: [number, number][] | null =
    place.shapeType === "POLYGON" && Array.isArray(place.polygon)
      ? (place.polygon as { lat: number; lng: number }[]).map((p) => [
          p.lat,
          p.lng,
        ])
      : null;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/history")}
        sx={{ mb: 2 }}
      >
        {translate("buttons.back", "Back")}
      </Button>

      {/* ─── Info Banner ─── */}
      <Paper
        elevation={1}
        sx={{
          mb: 2,
          p: 2.5,
          borderLeft: `4px solid ${orangeColor}`,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        {/* User */}
        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={user.profilePicture || undefined}
              alt={user.fullname}
              sx={{ width: 44, height: 44 }}
            >
              {user.fullname?.[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user.fullname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Separator */}
        <Box
          sx={{
            width: "1px",
            height: 40,
            bgcolor: "divider",
            display: { xs: "none", md: "block" },
          }}
        />

        {/* Zone name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PlaceIcon sx={{ color: orangeColor, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {place.emoji} {place.name}
          </Typography>
        </Box>

        {/* Separator */}
        <Box
          sx={{
            width: "1px",
            height: 40,
            bgcolor: "divider",
            display: { xs: "none", md: "block" },
          }}
        />

        {/* Entry date — hoverable */}
        <Box
          onMouseEnter={() => handleHover("entry")}
          onMouseLeave={handleHoverEnd}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s",
            bgcolor:
              highlightedMarker === "entry"
                ? alpha(MARKER_COLORS.entry, 0.1)
                : "transparent",
            border: `1px solid ${highlightedMarker === "entry" ? MARKER_COLORS.entry : "transparent"}`,
            "&:hover": {
              bgcolor: alpha(MARKER_COLORS.entry, 0.08),
            },
          }}
        >
          <LoginIcon sx={{ color: MARKER_COLORS.entry, fontSize: 20 }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.2 }}
            >
              {translate("history.fz.enteredAt", "Entered")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dayjs(entry.enteredAt).format("DD/MM/YYYY HH:mm:ss")}
            </Typography>
          </Box>
        </Box>

        {/* Exit date or "Still inside" — hoverable */}
        <Box
          onMouseEnter={() =>
            handleHover(isStillInside ? "current" : "exit")
          }
          onMouseLeave={handleHoverEnd}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s",
            bgcolor:
              highlightedMarker === (isStillInside ? "current" : "exit")
                ? alpha(
                    MARKER_COLORS[isStillInside ? "current" : "exit"],
                    0.1
                  )
                : "transparent",
            border: `1px solid ${
              highlightedMarker === (isStillInside ? "current" : "exit")
                ? MARKER_COLORS[isStillInside ? "current" : "exit"]
                : "transparent"
            }`,
            "&:hover": {
              bgcolor: alpha(
                MARKER_COLORS[isStillInside ? "current" : "exit"],
                0.08
              ),
            },
          }}
        >
          {isStillInside ? (
            <MyLocationIcon
              sx={{ color: MARKER_COLORS.current, fontSize: 20 }}
            />
          ) : (
            <LogoutIcon sx={{ color: MARKER_COLORS.exit, fontSize: 20 }} />
          )}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.2 }}
            >
              {isStillInside
                ? translate("monitoring.position", "Position")
                : translate("history.fz.exitedAt", "Exited")}
            </Typography>
            {isStillInside ? (
              <Chip
                label={translate("monitoring.stillInside", "Still inside")}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {dayjs(entry.exitedAt).format("DD/MM/YYYY HH:mm:ss")}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Separator */}
        <Box
          sx={{
            width: "1px",
            height: 40,
            bgcolor: "divider",
            display: { xs: "none", md: "block" },
          }}
        />

        {/* Duration */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccessTimeIcon sx={{ color: "text.secondary", fontSize: 20 }} />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", lineHeight: 1.2 }}
            >
              {translate("history.fz.duration", "Duration")}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {durationStr}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ─── Map ─── */}
      <Paper sx={{ height: "calc(100vh - 240px)", minHeight: 500 }}>
        <MapContainer
          center={mapCenter}
          zoom={16}
          style={{ height: "100%", width: "100%", borderRadius: "inherit" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
          />

          <MapHighlighter
            highlighted={highlightedMarker}
            positions={markerPositions}
          />

          {/* Zone boundary */}
          {polygonPositions ? (
            <Polygon
              positions={polygonPositions}
              pathOptions={{
                color: orangeColor,
                fillColor: orangeColor,
                fillOpacity: 0.15,
                weight: 2,
                dashArray: "8, 4",
              }}
            />
          ) : (
            <Circle
              center={[place.latitude, place.longitude]}
              radius={place.radius}
              pathOptions={{
                color: orangeColor,
                fillColor: orangeColor,
                fillOpacity: 0.15,
                weight: 2,
                dashArray: "8, 4",
              }}
            />
          )}

          {/* User path */}
          {pathPositions.length > 1 && (
            <Polyline
              positions={pathPositions}
              color="#78909C"
              weight={3}
              opacity={0.6}
              dashArray="6, 4"
            />
          )}

          {/* Entry marker (green) */}
          {keyLocations?.entry && (
            <Marker
              position={[
                keyLocations.entry.latitude,
                keyLocations.entry.longitude,
              ]}
              icon={markerIcon(
                MARKER_COLORS.entry,
                "E",
                highlightedMarker === "entry"
              )}
              zIndexOffset={highlightedMarker === "entry" ? 1000 : 0}
            >
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: MARKER_COLORS.entry,
                      mb: 0.5,
                    }}
                  >
                    {translate("history.fz.entry", "Zone entry")}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {dayjs(entry.enteredAt).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {dayjs(entry.enteredAt).format("HH:mm:ss")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {translate(
                      "history.fz.entryPopup",
                      "User entered the forbidden zone"
                    )}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          )}

          {/* Exit marker (red) */}
          {keyLocations?.exit && (
            <Marker
              position={[
                keyLocations.exit.latitude,
                keyLocations.exit.longitude,
              ]}
              icon={markerIcon(
                MARKER_COLORS.exit,
                "S",
                highlightedMarker === "exit"
              )}
              zIndexOffset={highlightedMarker === "exit" ? 1000 : 0}
            >
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: MARKER_COLORS.exit,
                      mb: 0.5,
                    }}
                  >
                    {translate("history.fz.exit", "Zone exit")}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {dayjs(entry.exitedAt).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {dayjs(entry.exitedAt).format("HH:mm:ss")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {translate(
                      "history.fz.exitPopup",
                      "User exited the forbidden zone"
                    )}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          )}

          {/* Current position marker (blue) — only when still inside */}
          {keyLocations?.current && (
            <Marker
              position={[
                keyLocations.current.latitude,
                keyLocations.current.longitude,
              ]}
              icon={markerIcon(
                MARKER_COLORS.current,
                "P",
                highlightedMarker === "current"
              )}
              zIndexOffset={highlightedMarker === "current" ? 1000 : 0}
            >
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: MARKER_COLORS.current,
                      mb: 0.5,
                    }}
                  >
                    {translate(
                      "history.fz.currentPosition",
                      "Current position"
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {dayjs(keyLocations.current.timestamp).format(
                      "DD/MM/YYYY"
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {dayjs(keyLocations.current.timestamp).format("HH:mm:ss")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {translate(
                      "history.fz.stillInsidePopup",
                      "User is still inside the forbidden zone"
                    )}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </Paper>
    </Box>
  );
};
